import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import { api } from "../../common/setupApi";
import { selectUser } from "../user/userSlice";

const VISIBILITY_OPTIONS = {
  VISIBLE: "visible",
  HIDDEN: "hidden",
  DEACTIVATED: "deactivated",
};

const WIDTH_OPTIONS = {
  FULL: "full",
  HALF: "half",
};

const applyRetroCompat = (arr) =>
  arr.map((d) => ({
    ...d,
    width:
      d.width !== WIDTH_OPTIONS.FULL && d.width !== WIDTH_OPTIONS.HALF
        ? WIDTH_OPTIONS.FULL
        : d.width,
    visibility: d.visibility || VISIBILITY_OPTIONS.VISIBLE,
  }));

const filterVisible = (arr) =>
  arr.filter((d) => d.visibility === VISIBILITY_OPTIONS.VISIBLE);

export const getMods = createAsyncThunk(
  "mods/get",
  async (_, { getState, rejectWithValue }) => {
    const userPk = selectUser(getState()).data.pk;

    // ⚠️ voir getClubConfig si test local !

    return api
      .get("modules/")
      .then((res) => {
        return { mods: res.data, userPk };
      })
      .catch((error) => {
        return rejectWithValue(error?.response?.data);
      });
  },
  {
    condition: (params, { getState }) => {
      if (params?.ignoreCondition) {
        return true;
      }
      // https://redux-toolkit.js.org/api/createAsyncThunk#canceling-before-execution
      const { status } = selectMods(getState());
      if (status === "fulfilled" || status === "pending") {
        // Already fetched or in progress, don't need to re-fetch
        return false;
      }
    },
  }
);

// activation dans la dataplace
// ne fonctionne pas pour autre user du club
export const activateMod = (id, layoutType) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userPk = selectUser(state).data.pk;
    const layout = state.mods.layouts[layoutType];
    let newLayout;

    // option 1 : ajouter l'entré dans le layout
    // const newLayout = [...layout, { id, width: WIDTH_OPTIONS.FULL, visibility: VISIBILITY_OPTIONS.VISIBLE }];

    // option 2 : on met à jour le module dans le layout
    const foundInLocalStorage = layout.find((d) => d.id === id);

    if (foundInLocalStorage) {
      // Reordering : on met le module à la fin du layout
      // on met à jour la largeur à full
      const item = {
        ...foundInLocalStorage,
        width: WIDTH_OPTIONS.FULL,
        visibility: VISIBILITY_OPTIONS.VISIBLE,
      };
      const removed = layout.filter((d) => d.id !== id);
      newLayout = [...removed, item];
    } else {
      // ajout du module à la fin du layout
      newLayout = [
        ...layout,
        {
          id,
          width: WIDTH_OPTIONS.FULL,
          visibility: VISIBILITY_OPTIONS.VISIBLE,
        },
      ];
    }

    dispatch(modsActions.setLayout({ newLayout, layoutType, userPk }));
  };
};

// désactivation dans la dataplace ou configuration
export const deactivateMod = (id, layoutType) => {
  return async (dispatch, getState) => {
    const state = getState();
    const userPk = selectUser(state).data.pk;
    const layout = state.mods.layouts[layoutType];

    // Option 1 : Suppression
    // const newLayout = layout.filter((d) => d.id !== id);

    // Option 2 : Mise à jour : désactivation du module dans le layout
    // On ne supprime pas le module du localStorage, car on veut garder la config du user si réactivation
    // Pas besoin d'ajouter l'entré dans le layout si absente
    const newLayout = layout.map((d) =>
      d.id === id ? { ...d, visibility: VISIBILITY_OPTIONS.DEACTIVATED } : d
    );

    dispatch(modsActions.setLayout({ newLayout, layoutType, userPk }));
  };
};

// toggle dans le dashboard
export const toggleMod = (id, layoutType) => async (dispatch, getState) => {
  const state = getState();
  const userPk = selectUser(state).data.pk;
  const layout = state.mods.layouts[layoutType];
  let newLayout;

  // si le module est déjà dans le layout, on cache, sinon on affiche
  // On ne supprime pas l'entré car on veut garder la config du user si réaffichage

  const foundInLocalStorage = layout.find((d) => d.id === id);

  if (foundInLocalStorage) {
    const isVisible =
      foundInLocalStorage.visibility === VISIBILITY_OPTIONS.VISIBLE;
    if (isVisible) {
      // on cache le module
      newLayout = layout.map((d) =>
        d.id === id ? { ...d, visibility: VISIBILITY_OPTIONS.HIDDEN } : d
      );
    } else {
      // on affiche le module
      // on met à jour la largeur à full
      // on le met à la fin du layout
      const item = {
        ...foundInLocalStorage,
        width: WIDTH_OPTIONS.FULL,
        visibility: VISIBILITY_OPTIONS.VISIBLE,
      };
      const removed = layout.filter((d) => d.id !== id);
      newLayout = [...removed, item];
    }
  } else {
    newLayout = [
      ...layout,
      {
        id,
        width: WIDTH_OPTIONS.FULL,
        visibility: VISIBILITY_OPTIONS.VISIBLE,
      },
    ];
  }

  dispatch(modsActions.setLayout({ newLayout, layoutType, userPk }));
};

export const adjustModWidth =
  (id, width, layoutType) => async (dispatch, getState) => {
    const state = getState();
    const userPk = selectUser(state).data.pk;
    const layout = state.mods.layouts[layoutType];

    const newLayout = layout.map((d) => (d.id === id ? { ...d, width } : d));

    dispatch(modsActions.setLayout({ newLayout, layoutType, userPk }));
  };

export const setModLegend =
  (id, legend, layoutType, merge) => async (dispatch, getState) => {
    const state = getState();
    const userPk = selectUser(state).data.pk;
    const layout = state.mods.layouts[layoutType];

    const newLayout = layout.map((d) => {
      if (d.id !== id) return d;
      const config = d.config || {};
      if (merge) {
        return {
          ...d,
          config: {
            ...config,
            legend: { ...(config.legend || {}), ...legend },
          },
        };
      } else {
        return { ...d, config: { ...config, legend } };
      }
    });

    dispatch(modsActions.setLayout({ newLayout, layoutType, userPk }));
  };

const initialState = {
  status: "idle",
  data: {},
  error: null,
  layouts: {},
};

const modsSlice = createSlice({
  name: "mods",
  initialState,
  reducers: {
    setMods: (state, action) => {
      state.data = action.payload;
    },
    setLayout: (state, action) => {
      const { newLayout, layoutType, userPk } = action.payload;
      state.layouts[layoutType] = newLayout;
      localStorage.setItem(
        `user${userPk}_${layoutType}_dash_layout`,
        JSON.stringify(newLayout)
      );
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMods.pending, (state) => {
        state.status = "pending";
        state.error = null;
      })
      .addCase(getMods.fulfilled, (state, action) => {
        state.status = "fulfilled";
        state.data = action.payload.mods;

        const userPk = action.payload.userPk;

        // NETTOYAGE DASH LAYOUT
        // si un autre membre du staff désactive un module, on le désactive pour les autres, on ne le supprime
        // pas car on garde la config du user si réactivation plus tard
        // on définit la bonne visibilité aux modules déjà présents dans le layout
        const activeColModIds = (action.payload.mods.coll || []).map(
          (d) => d.graph_id
        );
        const userColLayout =
          JSON.parse(localStorage.getItem(`user${userPk}_coll_dash_layout`)) ||
          [];

        const activeIndivModIds = (action.payload.mods.indiv || []).map(
          (d) => d.graph_id
        );
        const userIndivLayout =
          JSON.parse(localStorage.getItem(`user${userPk}_indiv_dash_layout`)) ||
          [];

        // Etape 1 : Nettoyage
        // option 1 : suppression
        // let newColLayout = userColLayout.filter((d) =>
        //   activeColModIds.includes(d.id)
        // );
        // let newIndivLayout = userIndivLayout.filter((d) =>
        //   activeIndivModIds.includes(d.id)
        // );

        // Option 2 : désactivation
        let newColLayout = userColLayout.map((d) => {
          const isModActive = activeColModIds.includes(d.id);
          return isModActive
            ? d
            : { ...d, visibility: VISIBILITY_OPTIONS.DEACTIVATED };
        });
        let newIndivLayout = userIndivLayout.map((d) => {
          const isModActive = activeIndivModIds.includes(d.id);
          return isModActive
            ? d
            : { ...d, visibility: VISIBILITY_OPTIONS.DEACTIVATED };
        });

        // Retrocompat
        newColLayout = applyRetroCompat(newColLayout);
        newIndivLayout = applyRetroCompat(newIndivLayout);

        // LocalStorage
        localStorage.setItem(
          `user${userPk}_coll_dash_layout`,
          JSON.stringify(newColLayout)
        );
        localStorage.setItem(
          `user${userPk}_indiv_dash_layout`,
          JSON.stringify(newIndivLayout)
        );

        // State
        state.layouts.coll = newColLayout;
        state.layouts.indiv = newIndivLayout;
      })
      .addCase(getMods.rejected, (state, action) => {
        state.status = "rejected";
        state.error = action.payload;
      })
      .addCase("auth/logout", () => initialState);
  },
});

export const modsActions = modsSlice.actions;

const _selectMods = (state) => state.mods;

export const selectMods = createSelector(
  [(state) => _selectMods(state)],
  (mods) => {
    const data = mods.data;
    const status = mods.status;
    const error = mods.error;
    const isLoading = status === "pending";
    return { data, status, error, isLoading };
  }
);

// memo ok
// https://redux.js.org/usage/deriving-data-selectors#writing-memoized-selectors-with-reselect
export const selectModsById = createSelector(
  [
    (state) => selectMods(state).data.coll || [],
    (state) => selectMods(state).data.indiv || [],
  ],
  (colMods, indivMods) => ({
    colMods: colMods.map((d) => d.graph_id),
    indivMods: indivMods.map((d) => d.graph_id),
  })
);

const _selectLayouts = (state) => state.mods.layouts;

export const selectLayouts = createSelector(
  [(state) => _selectLayouts(state)],
  (layouts) => {
    return {
      colLayout: filterVisible(layouts.coll || []),
      indivLayout: filterVisible(layouts.indiv || []),
    };
  }
);

export const selectColModWidth = createSelector(
  (state) => selectLayouts(state).colLayout,
  (_, modId) => modId,
  (layout, modId) => layout.find((d) => d.id === modId)?.width || "full"
);

export const selectIndivModWidth = createSelector(
  (state) => selectLayouts(state).indivLayout,
  (_, modId) => modId,
  (layout, modId) => layout.find((d) => d.id === modId)?.width || "full"
);

export const selectColModConfig = createSelector(
  (state) => selectLayouts(state).colLayout,
  (_, modId) => modId,
  (layout, modId) => layout.find((d) => d.id === modId)?.config || {}
);

export const selectIndivModConfig = createSelector(
  (state) => selectLayouts(state).indivLayout,
  (_, modId) => modId,
  (layout, modId) => layout.find((d) => d.id === modId)?.config || {}
);

export default modsSlice.reducer;
